import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [logoLoaded, setLogoLoaded] = useState(false);

  const scrollToSection = (sectionId: string) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-[#1a4d2e] text-white py-12" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            {!logoLoaded && (
              <div className="h-12 w-32 bg-white/20 animate-pulse rounded mb-4"></div>
            )}
            <img 
              src="https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/1573c889302d043df00450a85c1eb449.png" 
              alt="ProEditions Logo - Expert Business Writing Services" 
              className={`h-12 mb-4 transition-opacity duration-300 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              onLoad={() => setLogoLoaded(true)}
            />
            <p className="text-white/80 mb-4">
              Expert business writing services for entrepreneurs, small businesses, and non-profit organizations.
            </p>
            <div className="flex gap-4" role="list" aria-label="Social media links">
              <a 
                href="https://www.linkedin.com/company/proeditions-llc/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e]"
                aria-label="Visit ProEditions on LinkedIn"
              >
                <i className="ri-linkedin-fill text-xl" aria-hidden="true"></i>
              </a>
              <a 
                href="https://www.facebook.com/share/1GNWZHem7S/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e]"
                aria-label="Visit ProEditions on Facebook"
              >
                <i className="ri-facebook-fill text-xl" aria-hidden="true"></i>
              </a>
              <a 
                href="mailto:chanelle@proeditionssc.com"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e]"
                aria-label="Email ProEditions at chanelle@proeditionssc.com"
              >
                <i className="ri-mail-fill text-xl" aria-hidden="true"></i>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">Services</h3>
            <nav aria-label="Services navigation">
              <ul className="space-y-2 text-white/80">
                <li>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Grant Writing service"
                  >
                    Grant Writing
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Application Writing and Development service"
                  >
                    Application Writing & Development
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Custom Requests service"
                  >
                    Custom Requests
                  </button>
                </li>
              </ul>
            </nav>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <nav aria-label="Quick links navigation">
              <ul className="space-y-2 text-white/80">
                <li>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Services section"
                  >
                    Services
                  </button>
                </li>
                <li>
                  <Link 
                    to="/about"
                    className="hover:text-white cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1 inline-block"
                    aria-label="Navigate to About page"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('client-highlights')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Success Stories section"
                  >
                    Success Stories
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="hover:text-white cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1a4d2e] rounded-md px-2 py-1"
                    aria-label="Navigate to Contact section"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        
        <div className="border-t border-white/20 mt-8 pt-8 text-center text-white/80">
          <p>&copy; 2026 ProEditions. All rights reserved. <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-md px-1" aria-label="Website powered by Readdy">Powered by Readdy</a></p>
        </div>
      </div>
    </footer>
  );
}
